// Date display helpers that avoid the date-only / no-offset UTC off-by-one trap.
//
// `new Date("2026-05-26")` parses a date-only ISO string as UTC midnight, so
// formatting it in a negative-offset timezone (e.g. America/Montevideo, UTC−3)
// rolls it back to the previous calendar day ("25 may"). And a datetime with a
// time but no offset (e.g. "2026-06-02T21:47:11" — the backend emits UTC without
// a `Z`) is parsed by JS as LOCAL time, rendering hours off.
//
// These helpers treat date-only values as calendar dates (formatted in UTC, so
// the day never shifts) and no-offset datetimes as UTC instants.

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TIME_RE = /\d{1,2}:\d{2}/;
const HAS_TZ_RE = /[zZ]$|[+-]\d{2}:?\d{2}$/;

/**
 * Parse an API date string into a Date. A datetime that has a time component but
 * no timezone offset is treated as UTC. Date-only and offset-bearing strings are
 * parsed as-is. Returns null for empty/invalid input.
 * @param {string|null|undefined} value
 * @returns {Date|null}
 */
export const parseApiDate = (value) => {
  if (value == null || value === '') return null;
  let s = String(value).trim();
  if (!DATE_ONLY_RE.test(s) && HAS_TIME_RE.test(s) && !HAS_TZ_RE.test(s)) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Format a date for display.
 * - Date-only ISO strings (`YYYY-MM-DD`) are formatted as calendar dates in UTC,
 *   so they don't shift a day in negative-offset timezones.
 * - Datetimes (no-offset assumed UTC) are formatted in the user's local zone.
 * @param {string|null|undefined} value
 * @param {{ locale?: string } & Intl.DateTimeFormatOptions} [opts]
 * @returns {string} formatted label, or '' for empty/invalid input.
 */
export const formatDisplayDate = (value, { locale = 'es-UY', ...options } = {}) => {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (DATE_ONLY_RE.test(s)) {
    const d = new Date(`${s}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(d);
  }
  const d = parseApiDate(s);
  if (!d) return '';
  return new Intl.DateTimeFormat(locale, options).format(d);
};
